import React, { useEffect, useMemo } from 'react';
import { FaMoneyBill } from 'react-icons/fa';

import CardHeading from '../../../shared/CardHeading';
import { calculateCashItemsDetails, calculateInsuranceItemsDetails } from './VisitItemsCalculations';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { getFixedCardCost } from '../../../constants/VisitsConstants';

const BillSummaryOld = ({ patientType, formikRef, selectedPayerData, selectedPatientData, cardOptions = [], depositBalance, updateDepositValues = false, className = 'col-span-12' }) => {
    const { values, setFieldValue } = useFormikContext();
    const { t } = useTranslation();

    const cardCost = useMemo(() => getFixedCardCost(cardOptions, values?.cardType), [cardOptions, values?.cardType]);

    const cashDetails = useMemo(() => calculateCashItemsDetails(values, cardCost), [values?.items, values?.deduct_from_deposit, cardCost]);
    const insuranceDetails = useMemo(
        () => calculateInsuranceItemsDetails(values, selectedPayerData?.max_limit, selectedPatientData, cardCost),
        [values?.items, values?.deduct_from_deposit, selectedPayerData, selectedPatientData, cardCost]
    );

    useEffect(() => {
        if (!updateDepositValues) return
        if (depositBalance > 0) {
            setFieldValue('deduct_from_deposit', true)
            setFieldValue('paymentMethod', 'Deposit')
            setFieldValue('cardType', '')
            setFieldValue('cards', [])
        } else {
            setFieldValue('deduct_from_deposit', false)
            setFieldValue('paymentMethod', 'card')
            setFieldValue('cardType', 'Mada')
        }
    }, [depositBalance, selectedPatientData])


    return (
        <div className={`bg-white border border-gray-200 h-fit p-2 rounded-md shadow-md w-full md:col-span-4 xl:col-span-3 ${className}`}>
            <div className='bg-gray-100 px-2 py-1 rounded-md mt-1'>
                <div className='flex justify-between items-center'>
                    <h6 className='text-14-gray !font-[600]'>{t('TotalGrossAmount')} </h6>
                    <p className='text-14-dark'>
                        {patientType === 'cash'
                            ? cashDetails?.totals?.totalGrossAmount?.toFixed(2)
                            : insuranceDetails.totals?.totalGrossAmount?.toFixed(2)}
                    </p>
                </div>
                <div className='flex justify-between items-center my-1'>
                    <h6 className='text-14-gray !font-[600]'>{t('TotalDiscountAmount')}</h6>
                    <p className='text-14-dark'>
                        {patientType === 'cash'
                            ? values?.items
                                ?.map(itm => {
                                    const discountAmount = Number(itm?.discount_amount) || 0;
                                    const discountType = itm?.discount_type;
                                    let discount = 0;

                                    if (discountType === 'percentage') {
                                        discount = (Number(itm.unit_price) * Number(itm.quantity) * discountAmount) / 100;
                                    } else if (discountType === 'fixed') {
                                        discount = discountAmount;
                                    }

                                    return discount;
                                })
                                .reduce((acc, curr) => acc + curr, 0)?.toFixed(2)
                            : insuranceDetails.totals?.discountAmountTotal?.toFixed(2)}
                    </p>
                </div>
            </div>
            {
                patientType == 'cash' ? (
                    <div>
                        <div className='bg-gray-100 px-2 py-1 rounded-md mt-1'>
                            <h4 className='text-16-dark !font-[600]'>{t('PayerShare')}</h4>
                            <div className='flex items-center justify-between my-1'>
                                <h6 className='text-14-gray'>{t('NetPayable')}</h6>
                                <p className='text-14-dark'>{0}</p>
                            </div>
                            <div className='flex items-center justify-between my-1'>
                                <h6 className='text-14-gray'>{t('Vat')}</h6>
                                <p className='text-14-dark'>{0}</p>
                            </div>
                            <div className='flex justify-between items-center bg-primary/10 py-1 px-2 rounded-md my-1'>
                                <h6 className='text-14-gray font-bold text-gray-700'>{t('NetPayableWithVat')}</h6>
                                <p className='text-lg md:text-xl font-extrabold text-primary tabular-nums'>
                                    0.00
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className='bg-gray-100 px-2 py-1 rounded-md mt-1'>
                            <h4 className='text-16-dark !font-[600]'>{t('PayerShare')}</h4>
                            <div className='flex items-center justify-between my-1'>
                                <h6 className='text-14-gray'>{t('NetPayable')}</h6>
                                <p className='text-14-dark'>{insuranceDetails.totals?.companyShareTotal}</p>
                            </div>
                            <div className='flex items-center justify-between my-1'>
                                <h6 className='text-14-gray'>{t('Vat')}</h6>
                                <p className='text-14-dark'>{insuranceDetails.totals?.companyTaxTotal}</p>
                            </div>
                            <div className='flex justify-between items-center bg-primary/10 py-1 px-2 rounded-md my-1'>
                                <h6 className='text-14-gray font-bold text-gray-700'>{t('NetPayableWithVat')}</h6>
                                <p className='text-lg md:text-xl font-extrabold text-primary tabular-nums'>
                                    {(
                                        parseFloat(insuranceDetails.totals?.companyShareTotal || 0) +
                                        parseFloat(insuranceDetails.totals?.companyTaxTotal || 0)
                                    )?.toFixed(2)}
                                </p>
                            </div>
                        </div>
                    </div>
                )
            }
            <div className='bg-gray-100 px-2 py-1 rounded-md mt-1'>
                <h4 className='text-16-dark !font-[600]'>{t('PatientShare')}</h4>
                <div className='flex justify-between items-center my-1'>
                    <h6 className='text-14-gray'>{t('NetPayable')} </h6>
                    <p className='text-14-dark'>
                        {patientType === 'cash'
                            ? cashDetails?.totals?.patientShareTotal
                            : insuranceDetails.totals?.patientShareTotal}
                    </p>
                </div>
                <div className='flex justify-between items-center my-1'>
                    <h6 className='text-14-gray'>{t('Vat')}</h6>
                    <p className='text-14-dark'>
                        {patientType === 'cash' ? cashDetails?.totals?.patientTaxTotal : insuranceDetails.totals?.patientTaxTotal}
                    </p>
                </div>
                <div className='flex justify-between items-center bg-primary/10 py-1 px-2 rounded-md my-1'>
                    <h6 className='text-14-gray font-bold text-gray-700'>{t('NetPayableWithVat')}</h6>
                    <p className='text-lg md:text-xl font-extrabold text-primary tabular-nums'>
                        {patientType === 'cash'
                            ? Number(
                                cashDetails?.totals?.netPayableAmount +
                                cashDetails?.totals?.patientTaxTotal || 0
                            ).toFixed(2)
                            : Number(
                                insuranceDetails?.totals?.patientShareTotal + insuranceDetails?.totals?.patientTaxTotal || 0
                            ).toFixed(2)}
                    </p>
                </div>
            </div>


        </div>
    );
};

export default React.memo(BillSummaryOld);